/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Description as PrescriptionIcon,
  Healing as DischargeIcon,
  HealthAndSafety as WellnessIcon,
  LocalHospital as OPIcon,
  Receipt as InvoiceIcon,
  Science as DiagnosticIcon,
  UploadFile as DocIcon,
  Vaccines as ImmunizationIcon,
} from '@mui/icons-material';
import { useCallback, useEffect, useRef, useState, type ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
import type { ConsultationPushBody, FhirBundleType } from '@/api/clinical';
import { api } from '@/api/client';

interface CommonContext {
  tenantId: string;
  memberId: string;
  abhaAddress: string;
  abhaNumber: string;
  patientName: string;
  patientGender: string;
  patientBirthDate: string;
  facilityName: string;
  facilityId: string;
  practitionerName: string;
  practitionerId: string;
}

interface BundleCard {
  type: FhirBundleType;
  label: string;
  icon: ComponentType<SvgIconProps>;
  helperText: string;
}

const CARDS: BundleCard[] = [
  { type: 'Prescription',         label: 'Prescription',         icon: PrescriptionIcon, helperText: 'Medicines + dosage pattern + intake' },
  { type: 'OPConsultation',       label: 'OP Consultation',      icon: OPIcon,           helperText: 'ICD-10 + complaints + exam + history' },
  { type: 'DiagnosticReport',     label: 'Diagnostic Report',    icon: DiagnosticIcon,   helperText: 'Test results + reference ranges + PDF' },
  { type: 'HealthDocumentRecord', label: 'Health Document',      icon: DocIcon,          helperText: 'Free-form clinical document (PDF)' },
  { type: 'WellnessRecord',       label: 'Wellness Record',      icon: WellnessIcon,     helperText: 'Vitals + body measurements + lifestyle' },
  { type: 'Immunization',         label: 'Immunization',         icon: ImmunizationIcon, helperText: 'Vaccine name + lot + manufacturer + dose' },
  { type: 'DischargeSummary',     label: 'Discharge Summary',    icon: DischargeIcon,    helperText: 'Diagnoses + procedures + meds + follow-ups' },
  { type: 'Invoice',              label: 'Invoice',              icon: InvoiceIcon,      helperText: 'Items + GST + total amount' },
];

export function M2ConsultationForm({
  context,
  onSubmit,
  submitting,
}: {
  context: CommonContext;
  onSubmit: (body: ConsultationPushBody) => Promise<void>;
  submitting: boolean;
}) {
  const theme = useTheme();
  const [picked, setPicked] = useState<FhirBundleType | null>(null);
  const [practitioner, setPractitioner] = useState({ name: context.practitionerName, id: context.practitionerId });
  const [organisation, setOrganisation] = useState({ facilityName: context.facilityName, facilityId: context.facilityId });

  const buildEnvelope = (clinical: Record<string, unknown>, fhirBundleType: FhirBundleType): ConsultationPushBody => {
    const consultationNumber = `${randomSlug(8)}-${Date.now()}`;
    const encounter = `OPD-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${randomSlug(4)}`;
    const visitDate = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
    return {
      tenantId: context.tenantId,
      memberId: context.memberId,
      consultationNumber,
      status: 'CLOSED',
      appointmentDate: visitDate,
      linkedToABDM: true,
      abhaAddress: context.abhaAddress,
      abhaNumber: context.abhaNumber,
      fhirBundleType,
      clinical_data: {
        bundleType: bundleTypeName(fhirBundleType),
        careContextReference: consultationNumber,
        encounter,
        patient: {
          name: context.patientName,
          patientReference: context.abhaAddress,
          gender: context.patientGender,
          ...(context.patientBirthDate ? { birthDate: context.patientBirthDate } : {}),
        },
        practitioners: [{ name: practitioner.name, practitionerId: practitioner.id }],
        organisation: { facilityName: organisation.facilityName, facilityId: organisation.facilityId },
        visitDate,
        documents: [],
        ...clinical,
      },
    };
  };

  if (!picked) {
    return (
      <Stack spacing={3}>
        <ProviderCard practitioner={practitioner} setPractitioner={setPractitioner} organisation={organisation} setOrganisation={setOrganisation} />
        <Box>
          <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 1 }}>CHOOSE BUNDLE TYPE</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            {CARDS.map((c) => (
              <Box
                key={c.type}
                onClick={() => setPicked(c.type)}
                sx={{
                  p: 2,
                  borderRadius: theme.radius.md + 'px',
                  border: `1px solid ${theme.palette.border.subtle}`,
                  bgcolor: theme.palette.brand.lighter,
                  cursor: 'pointer',
                  transition: `border-color 120ms, transform 120ms`,
                  '&:hover': { borderColor: theme.palette.brand.main, transform: 'translateY(-1px)' },
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <c.icon sx={{ fontSize: 18, color: theme.palette.brand.dark }} />
                  <Typography variant="body2" fontWeight={600}>{c.label}</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">{c.helperText}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton size="small" onClick={() => setPicked(null)} aria-label="back to type picker"><BackIcon fontSize="small" /></IconButton>
        <Typography variant="h3" sx={{ fontSize: 16, fontWeight: 600 }}>{labelFor(picked)}</Typography>
        <Chip size="small" label="FHIR R4" color="info" variant="outlined" />
        <Chip size="small" label="ABDM PHR linked" color="success" variant="outlined" />
      </Stack>
      <BundleForm type={picked} onRun={(clinical) => onSubmit(buildEnvelope(clinical, picked))} submitting={submitting} />
    </Stack>
  );
}

function ProviderCard({
  practitioner,
  setPractitioner,
  organisation,
  setOrganisation,
}: {
  practitioner: { name: string; id: string };
  setPractitioner: (p: { name: string; id: string }) => void;
  organisation: { facilityName: string; facilityId: string };
  setOrganisation: (o: { facilityName: string; facilityId: string }) => void;
}) {
  return (
    <Stack spacing={1.5}>
      <Typography variant="overline" color="text.tertiary">PRACTITIONER</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField size="small" label="Practitioner Name" value={practitioner.name} onChange={(e) => setPractitioner({ ...practitioner, name: e.target.value })} sx={{ flex: 1 }} />
        <TextField size="small" label="Practitioner ID" value={practitioner.id} onChange={(e) => setPractitioner({ ...practitioner, id: e.target.value })} sx={{ flex: 1 }} />
      </Stack>
      <Typography variant="overline" color="text.tertiary">ORGANISATION</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField size="small" label="Facility Name" value={organisation.facilityName} onChange={(e) => setOrganisation({ ...organisation, facilityName: e.target.value })} sx={{ flex: 1 }} />
        <TextField size="small" label="Facility ID" value={organisation.facilityId} onChange={(e) => setOrganisation({ ...organisation, facilityId: e.target.value })} sx={{ flex: 1 }} />
      </Stack>
    </Stack>
  );
}

function BundleForm({ type, onRun, submitting }: { type: FhirBundleType; onRun: (clinical: Record<string, unknown>) => void; submitting: boolean }) {
  switch (type) {
    case 'Prescription':         return <PrescriptionForm onRun={onRun} submitting={submitting} />;
    case 'OPConsultation':       return <OPConsultationForm onRun={onRun} submitting={submitting} />;
    case 'DiagnosticReport':     return <DiagnosticReportForm onRun={onRun} submitting={submitting} />;
    case 'HealthDocumentRecord': return <HealthDocumentForm onRun={onRun} submitting={submitting} />;
    case 'WellnessRecord':       return <WellnessRecordForm onRun={onRun} submitting={submitting} />;
    case 'Immunization':         return <ImmunizationForm onRun={onRun} submitting={submitting} />;
    case 'DischargeSummary':     return <DischargeSummaryForm onRun={onRun} submitting={submitting} />;
    case 'Invoice':              return <InvoiceForm onRun={onRun} submitting={submitting} />;
  }
}

// ─── shared bits ─────────────────────────────────────────────────

function ActionRow({ onRun, submitting, disabled }: { onRun: () => void; submitting: boolean; disabled?: boolean }) {
  return (
    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
      <Button variant="contained" color="primary" disabled={submitting || disabled} onClick={onRun}>
        {submitting ? 'Pushing…' : 'Push to ABDM'}
      </Button>
    </Stack>
  );
}

const ENCOUNTER_TYPES = ['Outpatient', 'Inpatient', 'Emergency', 'Ambulatory', 'Day Care', 'Home Health', 'Observation', 'Pre-admission', 'Virtual', 'Field'];

const DOSAGE_OPTIONS = [
  { value: '1-0-0', label: 'Morning Only (1-0-0)' },
  { value: '1-0-1', label: 'Morning & Evening (1-0-1)' },
  { value: '1-1-1', label: 'Three Times Daily (1-1-1)' },
  { value: '0-0-1', label: 'Bedtime Only (0-0-1)' },
  { value: 'Q6H',   label: 'Every 6 Hours (Q6H)' },
  { value: 'Q4H',   label: 'Every 4 Hours (Q4H)' },
  { value: 'PRN',   label: 'As Needed (PRN)' },
];

const ROUTE_OPTIONS = ['Oral', 'Topical', 'Inhalation', 'Intravenous', 'Subcutaneous', 'Intramuscular'];
const METHOD_OPTIONS = ['Swallow', 'Drink', 'Apply', 'Inhale', 'Inject', 'Chew', 'Dissolve'];
const QUANTITY_OPTIONS = ['1/2', '1', '1 1/2', '2', '3', '4'];
const INTAKE_OPTIONS = ['Before Food', 'After Food', 'Empty Stomach', 'Bed Time', 'With Food'];
const PERIOD_OPTIONS = [
  { value: 'D', label: 'Days' },
  { value: 'WK', label: 'Weeks' },
  { value: 'MO', label: 'Months' },
  { value: 'H', label: 'Hours' },
  { value: 'MIN', label: 'Minutes' },
];

// ─── SNOMED Autocomplete ──────────────────────────────────────────

interface SnomedOption { code: string; display: string }

function normalizeSnomedResponse(data: any): SnomedOption[] {
  if (!data) return [];
  const keys = ['snomedMedicineCodes','snomedConditionProcedureCodes','snomedVaccineCodes',
    'snomedObservationCodes','snomedDiagnosticCodes','snomedEncounterCodes',
    'snomedMedicineRouteCodes','snomedSpecimenCodes'];
  for (const k of keys) {
    if (Array.isArray(data[k]) && data[k].length > 0)
      return data[k].map((x: any) => ({ code: x.code || '', display: x.display || '' }));
  }
  return [];
}

function SnomedAutocomplete({
  label,
  resource,
  value,
  onChange,
  required,
}: {
  label: string;
  resource: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  const [options, setOptions] = useState<SnomedOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchOptions = useCallback(async (q: string) => {
    if (q.length < 2) { setOptions([]); return; }
    setLoading(true);
    try {
      const res = await api.get(`/api/abdm/fhir/snomed/${encodeURIComponent(resource)}?q=${encodeURIComponent(q)}`);
      setOptions(normalizeSnomedResponse(res.data));
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [resource]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOptions(inputValue), 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [inputValue, fetchOptions]);

  return (
    <Autocomplete
      freeSolo
      options={options}
      getOptionLabel={(o) => typeof o === 'string' ? o : o.display}
      inputValue={inputValue}
      onInputChange={(_, v) => { setInputValue(v); onChange(v); }}
      onChange={(_, v) => {
        const display = v ? (typeof v === 'string' ? v : v.display) : '';
        setInputValue(display);
        onChange(display);
      }}
      loading={loading}
      filterOptions={(x) => x}
      slotProps={{ popper: { style: { zIndex: 1400 } } }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (<>{loading ? <CircularProgress color="inherit" size={14} /> : null}{params.InputProps.endAdornment}</>),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.code}>
          <Stack>
            <Typography variant="body2">{option.display}</Typography>
            <Typography variant="caption" color="text.secondary">{option.code}</Typography>
          </Stack>
        </Box>
      )}
    />
  );
}

// ─── Prescription ─────────────────────────────────────────────────

interface MedicationRow {
  name: string; dosagePattern: string;
  frequency: string; interval: string; period: string;
  route: string; method: string; quantity: string; intake: string;
  reason: string; additionalInstructions: string;
}
const blankMed = (): MedicationRow => ({
  name: '', dosagePattern: '1-0-0', frequency: '1', interval: '1', period: 'D',
  route: 'Oral', method: 'Swallow', quantity: '1', intake: 'After Food',
  reason: '', additionalInstructions: '',
});

export function PrescriptionForm({ onRun, submitting }: { onRun: (c: Record<string, unknown>) => void; submitting: boolean }) {
  const [encounterType, setEncounterType] = useState('Outpatient');
  const [intent, setIntent] = useState<'order' | 'plan' | 'proposal'>('order');
  const [status, setStatus] = useState<'active' | 'completed' | 'on-hold' | 'cancelled'>('active');
  const [priority, setPriority] = useState<'routine' | 'urgent' | 'asap' | 'stat'>('routine');
  const [authoredOn, setAuthoredOn] = useState<string>(toLocalDt(new Date()));
  const [meds, setMeds] = useState<MedicationRow[]>([blankMed()]);

  const update = (idx: number, patch: Partial<MedicationRow>) =>
    setMeds((p) => p.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  const addRow = () => setMeds((p) => [...p, blankMed()]);
  const removeRow = (i: number) => setMeds((p) => p.filter((_, idx) => idx !== i));

  // Build timing as a string "freq-interval-period" like old form → FHIR mapper expects String
  const buildTiming = (m: MedicationRow) => `${m.frequency}-${m.interval}-${m.period}`;

  const submit = () => onRun({
    encounterType,
    fhir: { resourceType: 'MedicationRequest', intent, status, priority, authoredOn: new Date(authoredOn).toISOString() },
    medications: meds.map((m) => ({
      medicineName: m.name,
      dosagePattern: m.dosagePattern,
      timing: buildTiming(m),
      route: m.route,
      method: m.method,
      quantity: m.quantity,
      intake: m.intake,
      reason: m.reason,
      additionalInstructions: [m.quantity && `Qty: ${m.quantity}`, m.intake, m.additionalInstructions].filter(Boolean).join(', '),
    })),
  });

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField select size="small" label="Encounter Type" value={encounterType} onChange={(e) => setEncounterType(e.target.value)} sx={{ flex: 1 }}>
          {ENCOUNTER_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField size="small" type="datetime-local" label="Authored On" InputLabelProps={{ shrink: true }} value={authoredOn} onChange={(e) => setAuthoredOn(e.target.value)} sx={{ flex: 1 }} />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField select size="small" label="Intent" value={intent} onChange={(e) => setIntent(e.target.value as typeof intent)} sx={{ flex: 1 }}>
          {['order', 'plan', 'proposal'].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)} sx={{ flex: 1 }}>
          {['active', 'completed', 'on-hold', 'cancelled'].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Priority" value={priority} onChange={(e) => setPriority(e.target.value as typeof priority)} sx={{ flex: 1 }}>
          {['routine', 'urgent', 'asap', 'stat'].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="overline" color="text.tertiary">PRESCRIPTIONS</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addRow}>Add Medicine</Button>
      </Stack>

      {meds.map((m, i) => (
        <Box key={i} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={1.5}>
            {/* Row 1: Medicine (SNOMED) + Dosage Pattern */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Box sx={{ flex: 2 }}>
                <SnomedAutocomplete label="Medicine / Drug Name *" resource="Medications" value={m.name} onChange={(v) => update(i, { name: v })} required />
              </Box>
              <TextField select size="small" label="Dosage Pattern *" value={m.dosagePattern} onChange={(e) => update(i, { dosagePattern: e.target.value })} sx={{ flex: 1 }}>
                {DOSAGE_OPTIONS.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
              </TextField>
            </Stack>

            {/* Row 2: Timing (Frequency × Interval × Period) */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 55 }}>Timing:</Typography>
              <TextField size="small" type="number" label="Frequency" value={m.frequency} onChange={(e) => update(i, { frequency: e.target.value })} sx={{ flex: 1 }} inputProps={{ min: 1 }} />
              <Typography variant="caption">×</Typography>
              <TextField size="small" type="number" label="Interval" value={m.interval} onChange={(e) => update(i, { interval: e.target.value })} sx={{ flex: 1 }} inputProps={{ min: 1 }} />
              <TextField select size="small" label="Period" value={m.period} onChange={(e) => update(i, { period: e.target.value })} sx={{ flex: 1 }}>
                {PERIOD_OPTIONS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </TextField>
            </Stack>

            {/* Row 3: Route + Method + Quantity + Intake */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField select size="small" label="Route *" value={m.route} onChange={(e) => update(i, { route: e.target.value })} sx={{ flex: 1 }}>
                {ROUTE_OPTIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Method *" value={m.method} onChange={(e) => update(i, { method: e.target.value })} sx={{ flex: 1 }}>
                {METHOD_OPTIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Quantity" value={m.quantity} onChange={(e) => update(i, { quantity: e.target.value })} sx={{ flex: 1 }}>
                {QUANTITY_OPTIONS.map((q) => <MenuItem key={q} value={q}>{q}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Intake" value={m.intake} onChange={(e) => update(i, { intake: e.target.value })} sx={{ flex: 1 }}>
                {INTAKE_OPTIONS.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
              </TextField>
            </Stack>

            {/* Row 4: Reason (SNOMED) + Additional Instructions + Delete */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <SnomedAutocomplete label="Reason *" resource="Condition" value={m.reason} onChange={(v) => update(i, { reason: v })} required />
              </Box>
              <TextField size="small" label="Additional Instructions" value={m.additionalInstructions} onChange={(e) => update(i, { additionalInstructions: e.target.value })} sx={{ flex: 2 }} />
              {meds.length > 1 && (
                <IconButton size="small" onClick={() => removeRow(i)} color="error" aria-label="remove medication">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          </Stack>
        </Box>
      ))}
      <ActionRow onRun={submit} submitting={submitting} disabled={meds.length === 0 || !meds[0]?.name} />
    </Stack>
  );
}

// ─── OPConsultation ───────────────────────────────────────────────

interface ComplaintRow { complaint: string }
interface AllergyRow { allergy: string; type: string; clinicalStatus: string }
interface PhysExamRow { observation: string; result: string }
interface MedHistRow { complaint: string }
interface FamilyHistRow { relationship: string; observation: string; age: string; isDeceased: string }
interface FollowupRow { serviceType: string; status: string; startDate: string; endDate: string; reason: string }
interface ProcedureRow { status: string; procedureReason: string; procedureName: string }
interface ReferralRow { status: string; details: string }
interface ObservationRow { observation: string; unit: string; value: string }
interface OPMedRow { medicine: string; dosage: string; timing: string; route: string; method: string; reason: string; additionalInstructions: string }

const blankOPMed = (): OPMedRow => ({ medicine: '', dosage: '1-0-0', timing: '1-1-D', route: 'Oral', method: 'Swallow', reason: '', additionalInstructions: '' });

export function OPConsultationForm({ onRun, submitting }: { onRun: (c: Record<string, unknown>) => void; submitting: boolean }) {
  const [icd10, setIcd10] = useState('');
  const [encounterType, setEncounterType] = useState('Outpatient');
  const [chiefComplaints, setChiefComplaints] = useState<ComplaintRow[]>([{ complaint: '' }]);
  const [physExams, setPhysExams] = useState<PhysExamRow[]>([{ observation: '', result: '' }]);
  const [allergies, setAllergies] = useState<AllergyRow[]>([{ allergy: '', type: 'medication', clinicalStatus: 'active' }]);
  const [medHistory, setMedHistory] = useState<MedHistRow[]>([{ complaint: '' }]);
  const [familyHistory, setFamilyHistory] = useState<FamilyHistRow[]>([{ relationship: '', observation: '', age: '', isDeceased: 'false' }]);
  const [medications, setMedications] = useState<OPMedRow[]>([blankOPMed()]);
  const [followups, setFollowups] = useState<FollowupRow[]>([{ serviceType: '', status: 'proposed', startDate: '', endDate: '', reason: '' }]);
  const [procedures, setProcedures] = useState<ProcedureRow[]>([{ status: 'COMPLETED', procedureReason: '', procedureName: '' }]);
  const [referrals] = useState<ReferralRow[]>([]);
  const [observations, setObservations] = useState<ObservationRow[]>([{ observation: '', unit: '', value: '' }]);
  const [otherObservations, setOtherObservations] = useState('');

  const submit = () => {
    const payload: Record<string, unknown> = { encounterType };
    if (icd10) payload.icd10Diagnosis = icd10;
    const cc = chiefComplaints.filter(c => c.complaint);
    if (cc.length) payload.chiefComplaints = cc.map(c => ({ complaint: c.complaint }));
    const pe = physExams.filter(p => p.observation);
    if (pe.length) payload.physicalExaminations = pe.map(p => ({ observation: p.observation, result: p.result }));
    const al = allergies.filter(a => a.allergy);
    if (al.length) payload.allergies = al.map(a => a.allergy);
    const mh = medHistory.filter(m => m.complaint);
    if (mh.length) payload.medicalHistories = mh.map(m => ({ complaint: m.complaint }));
    const fh = familyHistory.filter(f => f.relationship || f.observation);
    if (fh.length) payload.familyHistories = fh.map(f => ({ relationship: f.relationship, observation: f.observation, age: f.age, isDeceased: f.isDeceased, contributedToDeath: 'false' }));
    const meds = medications.filter(m => m.medicine);
    if (meds.length) payload.medications = meds.map(m => ({ medicine: m.medicine, dosage: m.dosage, timing: m.timing, route: m.route, method: m.method, reason: m.reason, additionalInstructions: m.additionalInstructions }));
    const fu = followups.filter(f => f.serviceType || f.reason);
    if (fu.length) payload.followups = fu.map(f => ({ serviceType: f.serviceType, status: f.status, startDate: f.startDate ? new Date(f.startDate).toISOString() : '', endDate: f.endDate ? new Date(f.endDate).toISOString() : '', reason: f.reason }));
    const pr = procedures.filter(p => p.procedureName);
    if (pr.length) payload.procedures = pr.map(p => ({ status: p.status, procedureReason: p.procedureReason, procedureName: p.procedureName }));
    const re = referrals.filter(r => r.details);
    if (re.length) payload.referrals = re.map(r => ({ status: r.status, details: r.details, specimen: '' }));
    const obs = observations.filter(o => o.observation);
    if (obs.length) payload.otherObservations = obs.map(o => ({ observation: o.observation, valueQuantity: { unit: o.unit, value: Number(o.value) || 0 } }));
    if (otherObservations) payload.otherObservationsText = otherObservations;
    onRun(payload);
  };

  return (
    <Stack spacing={2}>
      {/* ICD-10 + Encounter */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <SnomedAutocomplete label="ICD-10 Diagnosis" resource="Condition" value={icd10} onChange={setIcd10} />
        <TextField select size="small" label="Encounter Type" value={encounterType} onChange={(e) => setEncounterType(e.target.value)} sx={{ flex: 1, minWidth: 180 }}>
          {ENCOUNTER_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
      </Stack>

      {/* Chief Complaints */}
      <SectionTitle>Chief Complaints</SectionTitle>
      {chiefComplaints.map((c, i) => (
        <Stack key={i} direction="row" spacing={1} alignItems="center">
          <Box sx={{ flex: 1 }}>
            <SnomedAutocomplete label="Complaint" resource="Condition" value={c.complaint} onChange={(v) => setChiefComplaints(p => p.map((x, j) => j === i ? { complaint: v } : x))} />
          </Box>
          <IconButton size="small" onClick={() => setChiefComplaints(p => [...p, { complaint: '' }])}><AddIcon fontSize="small" /></IconButton>
          {chiefComplaints.length > 1 && <IconButton size="small" color="error" onClick={() => setChiefComplaints(p => p.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}
        </Stack>
      ))}

      {/* Physical Examinations */}
      <SectionTitle>Physical Examinations</SectionTitle>
      {physExams.map((p, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <Box sx={{ flex: 2 }}>
            <SnomedAutocomplete label="Observation" resource="Observations" value={p.observation} onChange={(v) => setPhysExams(prev => prev.map((x, j) => j === i ? { ...x, observation: v } : x))} />
          </Box>
          <TextField size="small" label="Result" value={p.result} onChange={(e) => setPhysExams(prev => prev.map((x, j) => j === i ? { ...x, result: e.target.value } : x))} sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setPhysExams(prev => [...prev, { observation: '', result: '' }])}><AddIcon fontSize="small" /></IconButton>
          {physExams.length > 1 && <IconButton size="small" color="error" onClick={() => setPhysExams(prev => prev.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}
        </Stack>
      ))}

      {/* Allergies */}
      <SectionTitle>Allergies</SectionTitle>
      {allergies.map((a, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <Box sx={{ flex: 2 }}>
            <SnomedAutocomplete label="Allergy / Substance" resource="Condition" value={a.allergy} onChange={(v) => setAllergies(prev => prev.map((x, j) => j === i ? { ...x, allergy: v } : x))} />
          </Box>
          <TextField select size="small" label="Type" value={a.type} onChange={(e) => setAllergies(prev => prev.map((x, j) => j === i ? { ...x, type: e.target.value } : x))} sx={{ flex: 1 }}>
            {['food', 'medication', 'environment', 'other'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Status" value={a.clinicalStatus} onChange={(e) => setAllergies(prev => prev.map((x, j) => j === i ? { ...x, clinicalStatus: e.target.value } : x))} sx={{ flex: 1 }}>
            {['active', 'inactive', 'resolved'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <IconButton size="small" onClick={() => setAllergies(p => [...p, { allergy: '', type: 'medication', clinicalStatus: 'active' }])}><AddIcon fontSize="small" /></IconButton>
          {allergies.length > 1 && <IconButton size="small" color="error" onClick={() => setAllergies(p => p.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}
        </Stack>
      ))}

      {/* Medical History */}
      <SectionTitle>Medical History</SectionTitle>
      {medHistory.map((m, i) => (
        <Stack key={i} direction="row" spacing={1} alignItems="center">
          <Box sx={{ flex: 1 }}>
            <SnomedAutocomplete label="Condition / History" resource="Condition" value={m.complaint} onChange={(v) => setMedHistory(p => p.map((x, j) => j === i ? { complaint: v } : x))} />
          </Box>
          <IconButton size="small" onClick={() => setMedHistory(p => [...p, { complaint: '' }])}><AddIcon fontSize="small" /></IconButton>
          {medHistory.length > 1 && <IconButton size="small" color="error" onClick={() => setMedHistory(p => p.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}
        </Stack>
      ))}

      {/* Family History */}
      <SectionTitle>Family History</SectionTitle>
      {familyHistory.map((f, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <TextField size="small" label="Relationship" value={f.relationship} onChange={(e) => setFamilyHistory(p => p.map((x, j) => j === i ? { ...x, relationship: e.target.value } : x))} sx={{ flex: 1 }} />
          <TextField size="small" label="Observation" value={f.observation} onChange={(e) => setFamilyHistory(p => p.map((x, j) => j === i ? { ...x, observation: e.target.value } : x))} sx={{ flex: 2 }} />
          <TextField size="small" type="number" label="Age" value={f.age} onChange={(e) => setFamilyHistory(p => p.map((x, j) => j === i ? { ...x, age: e.target.value } : x))} sx={{ flex: 0.7 }} />
          <TextField select size="small" label="Deceased" value={f.isDeceased} onChange={(e) => setFamilyHistory(p => p.map((x, j) => j === i ? { ...x, isDeceased: e.target.value } : x))} sx={{ flex: 0.8 }}>
            <MenuItem value="false">No</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
          </TextField>
          <IconButton size="small" onClick={() => setFamilyHistory(p => [...p, { relationship: '', observation: '', age: '', isDeceased: 'false' }])}><AddIcon fontSize="small" /></IconButton>
          {familyHistory.length > 1 && <IconButton size="small" color="error" onClick={() => setFamilyHistory(p => p.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}
        </Stack>
      ))}

      {/* Medication Summary */}
      <SectionTitle>Medication Summary</SectionTitle>
      {medications.map((m, i) => (
        <Box key={i} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={1}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Box sx={{ flex: 2 }}>
                <SnomedAutocomplete label="Medicine *" resource="Medications" value={m.medicine} onChange={(v) => setMedications(p => p.map((x, j) => j === i ? { ...x, medicine: v } : x))} required />
              </Box>
              <TextField select size="small" label="Dosage" value={m.dosage} onChange={(e) => setMedications(p => p.map((x, j) => j === i ? { ...x, dosage: e.target.value } : x))} sx={{ flex: 1 }}>
                {DOSAGE_OPTIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
              <TextField select size="small" label="Route" value={m.route} onChange={(e) => setMedications(p => p.map((x, j) => j === i ? { ...x, route: e.target.value } : x))} sx={{ flex: 1 }}>
                {ROUTE_OPTIONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Method" value={m.method} onChange={(e) => setMedications(p => p.map((x, j) => j === i ? { ...x, method: e.target.value } : x))} sx={{ flex: 1 }}>
                {METHOD_OPTIONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
              <Box sx={{ flex: 1 }}>
                <SnomedAutocomplete label="Reason" resource="Condition" value={m.reason} onChange={(v) => setMedications(p => p.map((x, j) => j === i ? { ...x, reason: v } : x))} />
              </Box>
              {medications.length > 1 && <IconButton size="small" color="error" onClick={() => setMedications(p => p.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}
            </Stack>
          </Stack>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={() => setMedications(p => [...p, blankOPMed()])} sx={{ alignSelf: 'flex-start' }}>Add Medicine</Button>

      {/* Follow-ups */}
      <SectionTitle>Follow-ups</SectionTitle>
      {followups.map((f, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <TextField size="small" label="Service Type" value={f.serviceType} onChange={(e) => setFollowups(p => p.map((x, j) => j === i ? { ...x, serviceType: e.target.value } : x))} sx={{ flex: 1 }} />
          <TextField select size="small" label="Status" value={f.status} onChange={(e) => setFollowups(p => p.map((x, j) => j === i ? { ...x, status: e.target.value } : x))} sx={{ flex: 1 }}>
            {['proposed', 'pending', 'booked', 'fulfilled', 'cancelled'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <TextField size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={f.startDate} onChange={(e) => setFollowups(p => p.map((x, j) => j === i ? { ...x, startDate: e.target.value } : x))} sx={{ flex: 1 }} />
          <TextField size="small" label="Reason" value={f.reason} onChange={(e) => setFollowups(p => p.map((x, j) => j === i ? { ...x, reason: e.target.value } : x))} sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setFollowups(p => [...p, { serviceType: '', status: 'proposed', startDate: '', endDate: '', reason: '' }])}><AddIcon fontSize="small" /></IconButton>
          {followups.length > 1 && <IconButton size="small" color="error" onClick={() => setFollowups(p => p.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}
        </Stack>
      ))}

      {/* Procedures */}
      <SectionTitle>Procedures</SectionTitle>
      {procedures.map((p, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <TextField select size="small" label="Status" value={p.status} onChange={(e) => setProcedures(prev => prev.map((x, j) => j === i ? { ...x, status: e.target.value } : x))} sx={{ flex: 1 }}>
            <MenuItem value="COMPLETED">Completed</MenuItem>
            <MenuItem value="INPROGRESS">In Progress</MenuItem>
          </TextField>
          <Box sx={{ flex: 2 }}>
            <SnomedAutocomplete label="Procedure Name" resource="Procedure" value={p.procedureName} onChange={(v) => setProcedures(prev => prev.map((x, j) => j === i ? { ...x, procedureName: v } : x))} />
          </Box>
          <Box sx={{ flex: 2 }}>
            <SnomedAutocomplete label="Procedure Reason" resource="Condition" value={p.procedureReason} onChange={(v) => setProcedures(prev => prev.map((x, j) => j === i ? { ...x, procedureReason: v } : x))} />
          </Box>
          <IconButton size="small" onClick={() => setProcedures(prev => [...prev, { status: 'COMPLETED', procedureReason: '', procedureName: '' }])}><AddIcon fontSize="small" /></IconButton>
          {procedures.length > 1 && <IconButton size="small" color="error" onClick={() => setProcedures(prev => prev.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}
        </Stack>
      ))}

      {/* Other Observations */}
      <SectionTitle>Other Observations</SectionTitle>
      {observations.map((o, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <Box sx={{ flex: 2 }}>
            <SnomedAutocomplete label="Observation" resource="Observations" value={o.observation} onChange={(v) => setObservations(p => p.map((x, j) => j === i ? { ...x, observation: v } : x))} />
          </Box>
          <TextField size="small" type="number" label="Value" value={o.value} onChange={(e) => setObservations(p => p.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} sx={{ flex: 1 }} />
          <TextField size="small" label="Unit" value={o.unit} onChange={(e) => setObservations(p => p.map((x, j) => j === i ? { ...x, unit: e.target.value } : x))} sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setObservations(p => [...p, { observation: '', unit: '', value: '' }])}><AddIcon fontSize="small" /></IconButton>
          {observations.length > 1 && <IconButton size="small" color="error" onClick={() => setObservations(p => p.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}
        </Stack>
      ))}

      <TextField size="small" label="Other Notes" multiline minRows={2} value={otherObservations} onChange={(e) => setOtherObservations(e.target.value)} />
      <ActionRow onRun={submit} submitting={submitting} disabled={!icd10} />
    </Stack>
  );
}

// ─── DiagnosticReport ─────────────────────────────────────────────

interface DiagnosticRow { testName: string; value: string; unit: string; referenceRange: string }
const blankDiag = (): DiagnosticRow => ({ testName: '', value: '', unit: '', referenceRange: '' });

export function DiagnosticReportForm({ onRun, submitting }: { onRun: (c: Record<string, unknown>) => void; submitting: boolean }) {
  const [reportName, setReportName] = useState('');
  const [authorizedBy, setAuthorizedBy] = useState('');
  const [category, setCategory] = useState<'LAB' | 'RAD' | 'CAR' | 'PAT' | 'GE' | 'NMR'>('LAB');
  const [fhirStatus, setFhirStatus] = useState<'preliminary' | 'final' | 'amended' | 'corrected'>('final');
  const [effectiveDateTime, setEffectiveDateTime] = useState<string>(toLocalDt(new Date()));
  const [results, setResults] = useState<DiagnosticRow[]>([blankDiag()]);
  const [impression, setImpression] = useState('');

  const addRow = () => setResults((p) => [...p, blankDiag()]);
  const removeRow = (i: number) => setResults((p) => p.filter((_, idx) => idx !== i));
  const update = (i: number, patch: Partial<DiagnosticRow>) => setResults((p) => p.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const submit = () => onRun({
    reportName, authorizedBy, results, impression,
    fhir: { resourceType: 'DiagnosticReport', category, status: fhirStatus, effectiveDateTime: new Date(effectiveDateTime).toISOString() },
  });

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField size="small" label="Report Name *" value={reportName} onChange={(e) => setReportName(e.target.value)} sx={{ flex: 1 }} />
        <TextField size="small" label="Authorized By" value={authorizedBy} onChange={(e) => setAuthorizedBy(e.target.value)} sx={{ flex: 1 }} />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField select size="small" label="Category" value={category} onChange={(e) => setCategory(e.target.value as typeof category)} sx={{ flex: 1 }}>
          <MenuItem value="LAB">Laboratory</MenuItem>
          <MenuItem value="RAD">Radiology</MenuItem>
          <MenuItem value="CAR">Cardiology</MenuItem>
          <MenuItem value="PAT">Pathology</MenuItem>
          <MenuItem value="GE">Genetics</MenuItem>
          <MenuItem value="NMR">Nuclear medicine</MenuItem>
        </TextField>
        <TextField select size="small" label="Status" value={fhirStatus} onChange={(e) => setFhirStatus(e.target.value as typeof fhirStatus)} sx={{ flex: 1 }}>
          {['preliminary', 'final', 'amended', 'corrected'].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
        <TextField size="small" type="datetime-local" label="Effective DateTime" InputLabelProps={{ shrink: true }} value={effectiveDateTime} onChange={(e) => setEffectiveDateTime(e.target.value)} sx={{ flex: 1 }} />
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="overline" color="text.tertiary">RESULTS</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addRow}>Add</Button>
      </Stack>
      {results.map((r, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <Box sx={{ flex: 2 }}>
            <SnomedAutocomplete label="Test Name *" resource="Diagnostics" value={r.testName} onChange={(v) => update(i, { testName: v })} required />
          </Box>
          <TextField size="small" label="Value *" value={r.value} onChange={(e) => update(i, { value: e.target.value })} sx={{ flex: 1 }} />
          <TextField size="small" label="Unit" value={r.unit} onChange={(e) => update(i, { unit: e.target.value })} sx={{ flex: 1 }} />
          <TextField size="small" label="Reference Range" value={r.referenceRange} onChange={(e) => update(i, { referenceRange: e.target.value })} sx={{ flex: 1 }} />
          {results.length > 1 && <IconButton size="small" color="error" onClick={() => removeRow(i)}><DeleteIcon fontSize="small" /></IconButton>}
        </Stack>
      ))}
      <TextField label="Impression / Conclusion" multiline minRows={2} value={impression} onChange={(e) => setImpression(e.target.value)} />
      <ActionRow onRun={submit} submitting={submitting} disabled={!reportName} />
    </Stack>
  );
}

// ─── HealthDocument ────────────────────────────────────────────────

export function HealthDocumentForm({ onRun, submitting }: { onRun: (c: Record<string, unknown>) => void; submitting: boolean }) {
  const [encounterType, setEncounterType] = useState('Outpatient');
  const [documentTitle, setDocumentTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onFile = (file: File | null) => {
    if (!file) { setPdfBase64(null); setFileName(null); return; }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => setPdfBase64(((e.target?.result ?? '') as string).split(',')[1] ?? null);
    reader.readAsDataURL(file);
  };

  const submit = () => {
    if (!pdfBase64) return;
    const docs = [{ contentType: 'application/pdf', type: documentTitle || 'HealthDocument', data: pdfBase64 }];
    onRun({ encounterType, documentTitle, description, documents: docs });
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1}>
        <TextField size="small" select label="Encounter Type" value={encounterType} onChange={(e) => setEncounterType(e.target.value)} sx={{ flex: 1 }}>
          {ENCOUNTER_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Document Title *" value={documentTitle} onChange={(e) => setDocumentTitle(e.target.value)} sx={{ flex: 2 }} />
      </Stack>

      {/* PDF Upload */}
      <Box sx={{ border: '2px dashed', borderColor: pdfBase64 ? 'success.main' : 'primary.main', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: pdfBase64 ? 'success.50' : 'primary.50' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          style={{ display: 'none' }}
          onChange={(e) => onFile(e.target.files?.[0] ?? null)}
        />
        {!pdfBase64 ? (
          <Stack spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">📄 Upload PDF Document <span style={{ color: '#e53935' }}>*</span></Typography>
            <Button variant="contained" size="small" onClick={() => fileInputRef.current?.click()}>
              Choose PDF File
            </Button>
            <Typography variant="caption" color="text.disabled">PDF required — binary data will be sent in FHIR attachment.data</Typography>
          </Stack>
        ) : (
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="success.main" fontWeight={600}>✅ {fileName}</Typography>
              <Button size="small" variant="outlined" color="error" onClick={() => { setPdfBase64(null); setFileName(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                Remove
              </Button>
            </Stack>
            <Box sx={{ width: '100%', height: 300, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <iframe
                src={`data:application/pdf;base64,${pdfBase64}`}
                width="100%"
                height="100%"
                title="PDF Preview"
                style={{ border: 'none' }}
              />
            </Box>
          </Stack>
        )}
      </Box>

      <TextField label="Description" multiline minRows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      <ActionRow onRun={submit} submitting={submitting} disabled={!documentTitle || !pdfBase64} />
    </Stack>
  );
}

// ─── WellnessRecord ───────────────────────────────────────────────

export function WellnessRecordForm({ onRun, submitting }: { onRun: (c: Record<string, unknown>) => void; submitting: boolean }) {
  const [vitals, setVitals] = useState({ respRate: '', heartRate: '', spo2: '', bodyTemp: '', sysBP: '', diaBP: '' });
  const [body, setBody] = useState({ height: '', weight: '', bmi: '', waist: '' });
  const [activity, setActivity] = useState({ steps: '', caloriesBurned: '', sleepHours: '' });
  const [general, setGeneral] = useState({ glucose: '', bodyFat: '', fluidIntake: '', calorieIntake: '' });
  const [women, setWomen] = useState({ menarcheAge: '', lastMenstrualDate: '' });
  const [lifestyle, setLifestyle] = useState({ dietType: '', tobacco: '', alcohol: '' });
  const [otherObservations, setOtherObservations] = useState('');

  const submit = () => onRun({ vitals, bodyMeasurements: body, physicalActivity: activity, generalAssessments: general, womensHealth: women, lifestyle, otherObservations });

  return (
    <Stack spacing={2}>
      <SectionTitle>Vital Signs</SectionTitle>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <NumField label="Respiratory rate /min" value={vitals.respRate} onChange={(v) => setVitals({ ...vitals, respRate: v })} />
        <NumField label="Heart rate /min" value={vitals.heartRate} onChange={(v) => setVitals({ ...vitals, heartRate: v })} />
        <NumField label="SpO₂ %" value={vitals.spo2} onChange={(v) => setVitals({ ...vitals, spo2: v })} />
        <NumField label="Body temperature °F" value={vitals.bodyTemp} onChange={(v) => setVitals({ ...vitals, bodyTemp: v })} />
        <NumField label="Systolic BP mmHg" value={vitals.sysBP} onChange={(v) => setVitals({ ...vitals, sysBP: v })} />
        <NumField label="Diastolic BP mmHg" value={vitals.diaBP} onChange={(v) => setVitals({ ...vitals, diaBP: v })} />
      </Stack>
      <SectionTitle>Body Measurements</SectionTitle>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <NumField label="Height cm" value={body.height} onChange={(v) => setBody({ ...body, height: v })} />
        <NumField label="Weight kg" value={body.weight} onChange={(v) => setBody({ ...body, weight: v })} />
        <NumField label="BMI kg/m²" value={body.bmi} onChange={(v) => setBody({ ...body, bmi: v })} />
        <NumField label="Waist cm" value={body.waist} onChange={(v) => setBody({ ...body, waist: v })} />
      </Stack>
      <SectionTitle>Physical Activity</SectionTitle>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <NumField label="Steps" value={activity.steps} onChange={(v) => setActivity({ ...activity, steps: v })} />
        <NumField label="Calories burned" value={activity.caloriesBurned} onChange={(v) => setActivity({ ...activity, caloriesBurned: v })} />
        <NumField label="Sleep hours" value={activity.sleepHours} onChange={(v) => setActivity({ ...activity, sleepHours: v })} />
      </Stack>
      <SectionTitle>General Assessments</SectionTitle>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <NumField label="Glucose mg/dL" value={general.glucose} onChange={(v) => setGeneral({ ...general, glucose: v })} />
        <NumField label="Body fat kg" value={general.bodyFat} onChange={(v) => setGeneral({ ...general, bodyFat: v })} />
        <NumField label="Fluid intake L" value={general.fluidIntake} onChange={(v) => setGeneral({ ...general, fluidIntake: v })} />
        <NumField label="Calorie intake kcal" value={general.calorieIntake} onChange={(v) => setGeneral({ ...general, calorieIntake: v })} />
      </Stack>
      <SectionTitle>Women's Health</SectionTitle>
      <Stack direction="row" spacing={1}>
        <TextField size="small" label="Age at menarche" value={women.menarcheAge} onChange={(e) => setWomen({ ...women, menarcheAge: e.target.value })} />
        <TextField size="small" type="date" label="Last menstrual date" InputLabelProps={{ shrink: true }} value={women.lastMenstrualDate} onChange={(e) => setWomen({ ...women, lastMenstrualDate: e.target.value })} />
      </Stack>
      <SectionTitle>Lifestyle</SectionTitle>
      <Stack direction="row" spacing={1}>
        <TextField size="small" label="Diet Type" value={lifestyle.dietType} onChange={(e) => setLifestyle({ ...lifestyle, dietType: e.target.value })} sx={{ flex: 1 }} />
        <TextField size="small" label="Tobacco use" value={lifestyle.tobacco} onChange={(e) => setLifestyle({ ...lifestyle, tobacco: e.target.value })} sx={{ flex: 1 }} />
        <TextField size="small" label="Alcohol consumption" value={lifestyle.alcohol} onChange={(e) => setLifestyle({ ...lifestyle, alcohol: e.target.value })} sx={{ flex: 1 }} />
      </Stack>
      <TextField label="Other Observations" multiline minRows={2} value={otherObservations} onChange={(e) => setOtherObservations(e.target.value)} />
      <ActionRow onRun={submit} submitting={submitting} />
    </Stack>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <Typography variant="overline" color="text.tertiary" sx={{ mt: 0.5 }}>{children}</Typography>;
}

function NumField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return <TextField size="small" type="number" label={label} value={value} onChange={(e) => onChange(e.target.value)} sx={{ flex: 1, minWidth: 160 }} />;
}

// ─── Immunization ─────────────────────────────────────────────────

interface ImmRow {
  vaccineName: string; lotNumber: string; manufacturer: string; doseNumber: string;
  occurrenceDateTime: string;
  primarySource: boolean;
  route: string; site: string;
  doseQuantityValue: string; doseQuantityUnit: string;
}
const blankImm = (): ImmRow => ({
  vaccineName: '', lotNumber: '', manufacturer: '', doseNumber: '1',
  occurrenceDateTime: toLocalDt(new Date()), primarySource: true,
  route: 'IM', site: 'LA', doseQuantityValue: '0.5', doseQuantityUnit: 'mL',
});

export function ImmunizationForm({ onRun, submitting }: { onRun: (c: Record<string, unknown>) => void; submitting: boolean }) {
  const [encounterType, setEncounterType] = useState('Outpatient');
  const [imms, setImms] = useState<ImmRow[]>([blankImm()]);
  const update = (i: number, patch: Partial<ImmRow>) => setImms((p) => p.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const submit = () => onRun({
    encounterType,
    immunizations: imms.map((r) => ({
      ...r,
      occurrenceDateTime: new Date(r.occurrenceDateTime).toISOString(),
      doseQuantity: { value: Number(r.doseQuantityValue), unit: r.doseQuantityUnit },
    })),
  });

  return (
    <Stack spacing={2}>
      <TextField size="small" select label="Encounter Type" value={encounterType} onChange={(e) => setEncounterType(e.target.value)} sx={{ maxWidth: 280 }}>
        {ENCOUNTER_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
      </TextField>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="overline" color="text.tertiary">IMMUNIZATIONS</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setImms((p) => [...p, blankImm()])}>Add</Button>
      </Stack>
      {imms.map((r, i) => (
        <Box key={i} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={1}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Box sx={{ flex: 2 }}>
                <SnomedAutocomplete label="Vaccine Name *" resource="Vaccines" value={r.vaccineName} onChange={(v) => update(i, { vaccineName: v })} required />
              </Box>
              <TextField size="small" label="Lot Number *" value={r.lotNumber} onChange={(e) => update(i, { lotNumber: e.target.value })} sx={{ flex: 1 }} />
              <TextField size="small" label="Manufacturer *" value={r.manufacturer} onChange={(e) => update(i, { manufacturer: e.target.value })} sx={{ flex: 1 }} />
              <TextField size="small" label="Dose # *" value={r.doseNumber} onChange={(e) => update(i, { doseNumber: e.target.value })} sx={{ flex: 0.8 }} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField size="small" type="datetime-local" label="Occurrence" InputLabelProps={{ shrink: true }} value={r.occurrenceDateTime} onChange={(e) => update(i, { occurrenceDateTime: e.target.value })} sx={{ flex: 1.5 }} />
              <TextField select size="small" label="Route" value={r.route} onChange={(e) => update(i, { route: e.target.value })} sx={{ flex: 1 }}>
                <MenuItem value="IM">Intramuscular</MenuItem>
                <MenuItem value="SC">Subcutaneous</MenuItem>
                <MenuItem value="IV">Intravenous</MenuItem>
                <MenuItem value="PO">Oral</MenuItem>
                <MenuItem value="ID">Intradermal</MenuItem>
              </TextField>
              <TextField select size="small" label="Site" value={r.site} onChange={(e) => update(i, { site: e.target.value })} sx={{ flex: 1 }}>
                <MenuItem value="LA">Left arm</MenuItem>
                <MenuItem value="RA">Right arm</MenuItem>
                <MenuItem value="LT">Left thigh</MenuItem>
                <MenuItem value="RT">Right thigh</MenuItem>
              </TextField>
              <TextField size="small" type="number" label="Dose qty" value={r.doseQuantityValue} onChange={(e) => update(i, { doseQuantityValue: e.target.value })} sx={{ flex: 0.8 }} />
              <TextField size="small" label="Unit" value={r.doseQuantityUnit} onChange={(e) => update(i, { doseQuantityUnit: e.target.value })} sx={{ flex: 0.6 }} />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormControlLabel
                control={<Checkbox size="small" checked={r.primarySource} onChange={(_, v) => update(i, { primarySource: v })} />}
                label={<Typography variant="caption">Primary source</Typography>}
              />
              {imms.length > 1 && <IconButton size="small" color="error" onClick={() => setImms((p) => p.filter((_, idx) => idx !== i))} sx={{ marginLeft: 'auto !important' }}><DeleteIcon fontSize="small" /></IconButton>}
            </Stack>
          </Stack>
        </Box>
      ))}
      <ActionRow onRun={submit} submitting={submitting} disabled={!imms[0]?.vaccineName} />
    </Stack>
  );
}

// ─── DischargeSummary ─────────────────────────────────────────────

export function DischargeSummaryForm({ onRun, submitting }: { onRun: (c: Record<string, unknown>) => void; submitting: boolean }) {
  const [icd10, setIcd10] = useState('');
  const [meds, setMeds] = useState<MedicationRow[]>([blankMed()]);
  const [dsComplaints, setDsComplaints] = useState<ComplaintRow[]>([{ complaint: '' }]);
  const [dsPhysExams, setDsPhysExams] = useState<PhysExamRow[]>([{ observation: '', result: '' }]);
  const [dsAllergies, setDsAllergies] = useState<AllergyRow[]>([{ allergy: '', type: 'medication', clinicalStatus: 'active' }]);
  const [dsMedHistory, setDsMedHistory] = useState<MedHistRow[]>([{ complaint: '' }]);
  const [dsProcedures, setDsProcedures] = useState<ProcedureRow[]>([{ status: 'COMPLETED', procedureReason: '', procedureName: '' }]);
  const [dsFamilyHistory, setDsFamilyHistory] = useState<FamilyHistRow[]>([{ relationship: '', observation: '', age: '', isDeceased: 'false' }]);
  const [diagnosticReports, setDiagnosticReports] = useState('');
  const [followUps, setFollowUps] = useState('');

  const updateMed = (idx: number, patch: Partial<MedicationRow>) => setMeds((p) => p.map((m, i) => (i === idx ? { ...m, ...patch } : m)));

  const submit = () => {
    const cc = dsComplaints.filter(c => c.complaint);
    const pe = dsPhysExams.filter(p => p.observation);
    const al = dsAllergies.filter(a => a.allergy);
    const mh = dsMedHistory.filter(m => m.complaint);
    const pr = dsProcedures.filter(p => p.procedureName);
    const fh = dsFamilyHistory.filter(f => f.relationship || f.observation);
    onRun({
      icd10Diagnosis: icd10,
      medicationSummary: meds.filter(m => m.name).map((m) => ({
        medicineName: m.name, dosagePattern: m.dosagePattern,
        timing: `${m.frequency}-${m.interval}-${m.period}`,
        route: m.route, method: m.method,
        reason: m.reason, additionalInstructions: m.additionalInstructions,
      })),
      chiefComplaints: cc.map(c => ({ complaint: c.complaint })),
      physicalExaminations: pe.map(p => ({ observation: p.observation, result: p.result })),
      allergies: al.map(a => a.allergy),
      medicalHistories: mh.map(m => ({ complaint: m.complaint })),
      procedures: pr.map(p => ({ status: p.status, procedureReason: p.procedureReason, procedureName: p.procedureName })),
      familyHistories: fh.map(f => ({ relationship: f.relationship, observation: f.observation, age: f.age, isDeceased: f.isDeceased, contributedToDeath: 'false' })),
      diagnosticReports: splitLines(diagnosticReports),
      followUps: splitLines(followUps),
    });
  };

  return (
    <Stack spacing={2}>
      <SnomedAutocomplete label="ICD-10 Diagnosis *" resource="Condition" value={icd10} onChange={setIcd10} required />

      <SectionTitle>Medication Summary</SectionTitle>
      {meds.map((m, i) => (
        <Box key={i} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={1}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Box sx={{ flex: 2 }}>
                <SnomedAutocomplete label="Medicine *" resource="Medications" value={m.name} onChange={(v) => updateMed(i, { name: v })} required />
              </Box>
              <TextField select size="small" label="Dosage" value={m.dosagePattern} onChange={(e) => updateMed(i, { dosagePattern: e.target.value })} sx={{ flex: 1 }}>
                {DOSAGE_OPTIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <SnomedAutocomplete label="Reason" resource="Condition" value={m.reason} onChange={(v) => updateMed(i, { reason: v })} />
              </Box>
              <TextField size="small" label="Additional Instructions" value={m.additionalInstructions} onChange={(e) => updateMed(i, { additionalInstructions: e.target.value })} sx={{ flex: 2 }} />
              {meds.length > 1 && <IconButton size="small" color="error" onClick={() => setMeds((p) => p.filter((_, idx) => idx !== i))}><DeleteIcon fontSize="small" /></IconButton>}
            </Stack>
          </Stack>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={() => setMeds((p) => [...p, blankMed()])} sx={{ alignSelf: 'flex-start' }}>Add medicine</Button>

      <SectionTitle>Chief Complaints</SectionTitle>
      {dsComplaints.map((c, i) => (
        <Stack key={i} direction="row" spacing={1} alignItems="center">
          <Box sx={{ flex: 1 }}>
            <SnomedAutocomplete label="Complaint" resource="Condition" value={c.complaint} onChange={(v) => setDsComplaints(p => p.map((x, j) => j === i ? { complaint: v } : x))} />
          </Box>
          <IconButton size="small" onClick={() => setDsComplaints(p => [...p, { complaint: '' }])}><AddIcon fontSize="small" /></IconButton>
          {dsComplaints.length > 1 && <IconButton size="small" color="error" onClick={() => setDsComplaints(p => p.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}
        </Stack>
      ))}

      <SectionTitle>Physical Examinations</SectionTitle>
      {dsPhysExams.map((p, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <Box sx={{ flex: 2 }}>
            <SnomedAutocomplete label="Observation" resource="Observations" value={p.observation} onChange={(v) => setDsPhysExams(prev => prev.map((x, j) => j === i ? { ...x, observation: v } : x))} />
          </Box>
          <TextField size="small" label="Result" value={p.result} onChange={(e) => setDsPhysExams(prev => prev.map((x, j) => j === i ? { ...x, result: e.target.value } : x))} sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setDsPhysExams(prev => [...prev, { observation: '', result: '' }])}><AddIcon fontSize="small" /></IconButton>
          {dsPhysExams.length > 1 && <IconButton size="small" color="error" onClick={() => setDsPhysExams(prev => prev.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}
        </Stack>
      ))}

      <SectionTitle>Allergies</SectionTitle>
      {dsAllergies.map((a, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <Box sx={{ flex: 2 }}>
            <SnomedAutocomplete label="Allergy / Substance" resource="Condition" value={a.allergy} onChange={(v) => setDsAllergies(prev => prev.map((x, j) => j === i ? { ...x, allergy: v } : x))} />
          </Box>
          <TextField select size="small" label="Type" value={a.type} onChange={(e) => setDsAllergies(prev => prev.map((x, j) => j === i ? { ...x, type: e.target.value } : x))} sx={{ flex: 1 }}>
            {['food', 'medication', 'environment', 'other'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Status" value={a.clinicalStatus} onChange={(e) => setDsAllergies(prev => prev.map((x, j) => j === i ? { ...x, clinicalStatus: e.target.value } : x))} sx={{ flex: 1 }}>
            {['active', 'inactive', 'resolved'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <IconButton size="small" onClick={() => setDsAllergies(p => [...p, { allergy: '', type: 'medication', clinicalStatus: 'active' }])}><AddIcon fontSize="small" /></IconButton>
          {dsAllergies.length > 1 && <IconButton size="small" color="error" onClick={() => setDsAllergies(p => p.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}
        </Stack>
      ))}

      <SectionTitle>Medical History</SectionTitle>
      {dsMedHistory.map((m, i) => (
        <Stack key={i} direction="row" spacing={1} alignItems="center">
          <Box sx={{ flex: 1 }}>
            <SnomedAutocomplete label="Condition / History" resource="Condition" value={m.complaint} onChange={(v) => setDsMedHistory(p => p.map((x, j) => j === i ? { complaint: v } : x))} />
          </Box>
          <IconButton size="small" onClick={() => setDsMedHistory(p => [...p, { complaint: '' }])}><AddIcon fontSize="small" /></IconButton>
          {dsMedHistory.length > 1 && <IconButton size="small" color="error" onClick={() => setDsMedHistory(p => p.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}
        </Stack>
      ))}

      <SectionTitle>Procedures</SectionTitle>
      {dsProcedures.map((p, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <TextField select size="small" label="Status" value={p.status} onChange={(e) => setDsProcedures(prev => prev.map((x, j) => j === i ? { ...x, status: e.target.value } : x))} sx={{ flex: 1 }}>
            <MenuItem value="COMPLETED">Completed</MenuItem>
            <MenuItem value="INPROGRESS">In Progress</MenuItem>
          </TextField>
          <Box sx={{ flex: 2 }}>
            <SnomedAutocomplete label="Procedure Name" resource="Procedure" value={p.procedureName} onChange={(v) => setDsProcedures(prev => prev.map((x, j) => j === i ? { ...x, procedureName: v } : x))} />
          </Box>
          <Box sx={{ flex: 2 }}>
            <SnomedAutocomplete label="Procedure Reason" resource="Condition" value={p.procedureReason} onChange={(v) => setDsProcedures(prev => prev.map((x, j) => j === i ? { ...x, procedureReason: v } : x))} />
          </Box>
          <IconButton size="small" onClick={() => setDsProcedures(prev => [...prev, { status: 'COMPLETED', procedureReason: '', procedureName: '' }])}><AddIcon fontSize="small" /></IconButton>
          {dsProcedures.length > 1 && <IconButton size="small" color="error" onClick={() => setDsProcedures(prev => prev.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}
        </Stack>
      ))}

      <SectionTitle>Family History</SectionTitle>
      {dsFamilyHistory.map((f, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <TextField size="small" label="Relationship" value={f.relationship} onChange={(e) => setDsFamilyHistory(p => p.map((x, j) => j === i ? { ...x, relationship: e.target.value } : x))} sx={{ flex: 1 }} />
          <TextField size="small" label="Observation" value={f.observation} onChange={(e) => setDsFamilyHistory(p => p.map((x, j) => j === i ? { ...x, observation: e.target.value } : x))} sx={{ flex: 2 }} />
          <TextField size="small" type="number" label="Age" value={f.age} onChange={(e) => setDsFamilyHistory(p => p.map((x, j) => j === i ? { ...x, age: e.target.value } : x))} sx={{ flex: 0.7 }} />
          <TextField select size="small" label="Deceased" value={f.isDeceased} onChange={(e) => setDsFamilyHistory(p => p.map((x, j) => j === i ? { ...x, isDeceased: e.target.value } : x))} sx={{ flex: 0.8 }}>
            <MenuItem value="false">No</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
          </TextField>
          <IconButton size="small" onClick={() => setDsFamilyHistory(p => [...p, { relationship: '', observation: '', age: '', isDeceased: 'false' }])}><AddIcon fontSize="small" /></IconButton>
          {dsFamilyHistory.length > 1 && <IconButton size="small" color="error" onClick={() => setDsFamilyHistory(p => p.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}
        </Stack>
      ))}

      <ListField label="Diagnostic Reports (one per line)" value={diagnosticReports} onChange={setDiagnosticReports} />
      <ListField label="Follow-ups (one per line)" value={followUps} onChange={setFollowUps} />
      <ActionRow onRun={submit} submitting={submitting} disabled={!icd10} />
    </Stack>
  );
}

// ─── Invoice ──────────────────────────────────────────────────────

interface InvoiceItem { description: string; quantity: string; unitPrice: string; mrp: string; discount: string; rate: string; totalPrice: string; cgst: string; sgst: string; netAmount: string }
const blankItem = (): InvoiceItem => ({ description: '', quantity: '1', unitPrice: '', mrp: '', discount: '', rate: '', totalPrice: '', cgst: '', sgst: '', netAmount: '' });

export function InvoiceForm({ onRun, submitting }: { onRun: (c: Record<string, unknown>) => void; submitting: boolean }) {
  const [encounterType, setEncounterType] = useState('Outpatient');
  const [transactionId, setTransactionId] = useState('');
  const [items, setItems] = useState<InvoiceItem[]>([blankItem()]);
  const [totalAmount, setTotalAmount] = useState('');
  const [currency, setCurrency] = useState('INR');

  const update = (i: number, patch: Partial<InvoiceItem>) => setItems((p) => p.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const submit = () => onRun({ encounterType, transactionId, items, totalAmount, currency });

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1}>
        <TextField size="small" select label="Encounter Type" value={encounterType} onChange={(e) => setEncounterType(e.target.value)} sx={{ flex: 1 }}>
          {ENCOUNTER_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Transaction ID *" value={transactionId} onChange={(e) => setTransactionId(e.target.value)} sx={{ flex: 1 }} />
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="overline" color="text.tertiary">INVOICE ITEMS</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setItems((p) => [...p, blankItem()])}>Add</Button>
      </Stack>
      {items.map((r, i) => (
        <Box key={i} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
              <TextField size="small" label="Description *" value={r.description} onChange={(e) => update(i, { description: e.target.value })} sx={{ flex: 3 }} />
              <TextField size="small" label="Qty *" value={r.quantity} onChange={(e) => update(i, { quantity: e.target.value })} sx={{ flex: 1 }} />
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField size="small" label="Unit Price *" value={r.unitPrice} onChange={(e) => update(i, { unitPrice: e.target.value })} sx={{ flex: 1 }} />
              <TextField size="small" label="MRP" value={r.mrp} onChange={(e) => update(i, { mrp: e.target.value })} sx={{ flex: 1 }} />
              <TextField size="small" label="Discount" value={r.discount} onChange={(e) => update(i, { discount: e.target.value })} sx={{ flex: 1 }} />
              <TextField size="small" label="Total Price *" value={r.totalPrice} onChange={(e) => update(i, { totalPrice: e.target.value })} sx={{ flex: 1 }} />
              <TextField size="small" label="CGST" value={r.cgst} onChange={(e) => update(i, { cgst: e.target.value })} sx={{ flex: 1 }} />
              <TextField size="small" label="SGST" value={r.sgst} onChange={(e) => update(i, { sgst: e.target.value })} sx={{ flex: 1 }} />
              <TextField size="small" label="Net Amount" value={r.netAmount} onChange={(e) => update(i, { netAmount: e.target.value })} sx={{ flex: 1 }} />
              {items.length > 1 && <IconButton size="small" color="error" onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))}><DeleteIcon fontSize="small" /></IconButton>}
            </Stack>
          </Stack>
        </Box>
      ))}
      <Stack direction="row" spacing={1}>
        <TextField size="small" label="Total Amount *" value={totalAmount} onChange={(e) => setTotalAmount(e.target.value)} sx={{ flex: 2 }} />
        <TextField size="small" select label="Currency *" value={currency} onChange={(e) => setCurrency(e.target.value)} sx={{ flex: 1 }}>
          {['INR', 'USD'].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
      </Stack>
      <ActionRow onRun={submit} submitting={submitting} disabled={!transactionId || !items[0]?.description} />
    </Stack>
  );
}

// ─── helpers ──────────────────────────────────────────────────────

function ListField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return <TextField size="small" label={label} multiline minRows={1} value={value} onChange={(e) => onChange(e.target.value)} />;
}

function splitLines(v: string): string[] {
  return v.split('\n').map((s) => s.trim()).filter(Boolean);
}

function toLocalDt(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function bundleTypeName(t: FhirBundleType): string {
  switch (t) {
    case 'Prescription':         return 'PrescriptionRecord';
    case 'OPConsultation':       return 'OPConsultationRecord';
    case 'DiagnosticReport':     return 'DiagnosticReportRecord';
    case 'HealthDocumentRecord': return 'HealthDocumentRecord';
    case 'WellnessRecord':       return 'WellnessRecord';
    case 'Immunization':         return 'ImmunizationRecord';
    case 'DischargeSummary':     return 'DischargeSummaryRecord';
    case 'Invoice':              return 'InvoiceRecord';
  }
}

function labelFor(t: FhirBundleType): string {
  return CARDS.find((c) => c.type === t)?.label ?? t;
}

function randomSlug(len: number): string {
  return Math.random().toString(36).slice(2, 2 + len).toUpperCase();
}
